<script setup lang="ts">
import { ref, watch, computed } from 'vue';

const props = defineProps<{
	label: string;
	modelValue: number | null; // pace in minutes per km or clock time in decimal hours
	unit?: string; // unit to display, defaults to 'min/km'
	isClockTime?: boolean; // true for HH:MM clock time, false for MM:SS pace
	placeholder?: string;
}>();

const emit = defineEmits<{
	'update:modelValue': [value: number | null];
}>();

// Convert decimal minutes to MM:SS format (for pace)
const formatPace = (minutes: number): string => {
	const mins = Math.floor(minutes);
	const secs = Math.round((minutes - mins) * 60);
	return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Convert decimal hours to HH:MM format (for clock time)
const formatClockTime = (decimalHours: number): string => {
	const hours = Math.floor(decimalHours);
	const mins = Math.round((decimalHours - hours) * 60);
	return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Unified format method that delegates based on mode
const formatValue = (value: number | null): string => {
	if (value === null) return '';
	if (props.isClockTime) {
		return formatClockTime(value);
	}
	return formatPace(value);
};

// Parse MM:SS format to decimal minutes (for pace)
const parsePace = (value: string): number => {
	const parts = value.split(':');

	if (parts.length === 2) {
		const mins = parseInt(parts[0]) || 0;
		const secs = parseInt(parts[1] || '0') || 0;
		return mins + Math.min(secs, 59) / 60;
	} else if (parts.length === 1 && parts[0]) {
		const mins = parseInt(parts[0]) || 0;
		return mins;
	}

	return 0;
};

// Parse HH:MM format to decimal hours (for clock time)
const parseClockTime = (value: string): number | null => {
	if (!value) return null;

	const parts = value.split(':');

	if (parts.length === 2) {
		const hours = (parseInt(parts[0]) || 0) % 24;
		const mins = parseInt(parts[1] || '0') || 0;
		return hours + Math.min(mins, 59) / 60;
	} else if (parts.length === 1 && parts[0]) {
		const num = parseInt(parts[0]) || 0;
		// Handle 3-4 digit numbers as HHMM or HMM (e.g., 1800 → 18:00, 930 → 9:30)
		if (num >= 100) {
			const hours = Math.floor(num / 100) % 24;
			const mins = num % 100;
			return hours + Math.min(mins, 59) / 60;
		}
		// Single/double digit: treat as hours
		const hours = num % 24;
		return hours;
	}

	return null;
};

// Unified parse method that delegates based on mode
const parseValue = (value: string): number | null => {
	if (props.isClockTime) {
		return parseClockTime(value);
	}
	return parsePace(value);
};

const inputValue = ref(formatValue(props.modelValue));
const isEditing = ref(false);

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
	// Only update the input if user is not actively editing
	if (!isEditing.value) {
		const formatted = formatValue(newValue);
		if (inputValue.value !== formatted) {
			inputValue.value = formatted;
		}
	}
});

const handleInput = (event: Event) => {
	isEditing.value = true;
	const target = event.target as HTMLInputElement;
	let value = target.value;

	// Convert period or comma to colon for easier input
	value = value.replace(/[.,]/g, ':');

	// Remove any non-digit and non-colon characters
	value = value.replace(/[^\d:]/g, '');

	// Ensure only one colon
	const colonCount = (value.match(/:/g) || []).length;
	if (colonCount > 1) {
		value = value.replace(/:.*:/, ':');
	}

	inputValue.value = value;

	// Emit the parsed value immediately
	const decimalValue = parseValue(value);
	emit('update:modelValue', decimalValue);
};

const handleBlur = () => {
	// Parse and validate on blur
	if (inputValue.value) {
		const decimalValue = parseValue(inputValue.value);
		emit('update:modelValue', decimalValue);
		// Re-format to ensure consistent display
		inputValue.value = formatValue(decimalValue);
	}
	isEditing.value = false;
};

const isValid = computed(() => {
	const parts = inputValue.value.split(':');
	return (parts.length === 2 && parts[0]) || (parts.length === 1 && parts[0]);
});
</script>

<template>
	<div class="pace-input">
		<label>{{ label }}</label>
		<div class="wrapper">
			<input type="text" :class="{ 'invalid': !isValid && inputValue }" v-model="inputValue" @input="handleInput"
				@blur="handleBlur" :placeholder="placeholder || '5:44'" inputmode="decimal" />
			<span class="unit" v-if="unit !== ''">{{ unit || 'min/km' }}</span>
		</div>
	</div>
</template>

<style scoped lang="scss">
.pace-input {
	margin-bottom: 0;
	margin-right: 1rem;
	flex: 0 1 auto;

	label {
		display: block;
		margin-bottom: 0.4rem;
		font-weight: 600;
		color: #555;
		text-transform: capitalize;
		font-size: 0.8rem;
	}

	.wrapper {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	input {
		flex: 1;
		padding: 0.5rem 0.5rem;
		border: 1px solid #ccc;
		border-radius: 4px;
		font-size: 1rem;
		font-family: monospace;
		transition: all 0.2s;
		width: 70px;
		background: #fff;
		font-weight: 500;

		&::placeholder {
			color: #bbb;
			opacity: 1;
		}

		&:focus {
			outline: none;
			border-color: #666;
			box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
		}

		&.invalid {
			border-color: #ff6b6b;
			background: #fff5f5;
		}
	}

	.unit {
		color: #666;
		font-size: 0.85rem;
		white-space: nowrap;
		font-weight: 500;
	}
}
</style>
